//  Client\client-vite\src\Calls\callDealerAuth.ts
import { API_URL, AUTH_DEALERS_BASE } from "@/config";
import axios, { AxiosError } from "axios";

/* --------------------------------------------
   Axios Setup
-------------------------------------------- */
declare module "axios" {
    export interface AxiosRequestConfig {
        suppress401Log?: boolean;
    }
}

export const http = axios.create({
    baseURL: API_URL, // e.g. http://localhost:3333/api
    withCredentials: true,
    headers: { "Content-Type": "application/json" },
});

/* --------------------------------------------
   Helper utilities
-------------------------------------------- */
function isRecord(v: unknown): v is Record<string, unknown> {
    return typeof v === "object" && v !== null;
}

function pickErrorMessage(err: unknown): string {
    if (axios.isAxiosError(err)) {
        const ax = err as AxiosError<unknown>;
        const data = ax.response?.data;
        if (typeof data === "string") return data;
        if (isRecord(data)) {
            if (typeof data.message === "string") return data.message;
            if (typeof data.error === "string") return data.error;
        }
        return ax.message || "Request failed";
    }
    return err instanceof Error ? err.message : "Request failed";
}

/* --------------------------------------------
   Types
-------------------------------------------- */
export interface DealerAuthUser {
    id: number;
    username: string;
    email: string | null;
    company: string | null;
}

export interface DealerAuthResponse {
    ok: boolean;
    message: string;
    dealer?: DealerAuthUser;
}

/* --------------------------------------------
   Dealer Auth Calls
-------------------------------------------- */
export async function loginDealer(email: string, password: string) {

    try {
        
        const res = await fetch(`${AUTH_DEALERS_BASE}/dealer-login`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ email, password }),
            credentials: "include",
        });
        if (!res.ok) throw new Error(`Login failed: ${res.status}`);
        return res.json();
        
    } catch (e) {
        throw new Error(pickErrorMessage(e));
    }
   
}

export async function getCurrentDealer(): Promise<DealerAuthUser | null> {
    try {
        const res = await http.get<{ ok: boolean; dealer?: DealerAuthUser }>(
            "/dealers-auth/me",
            {
                withCredentials: true,
                validateStatus: (s) => (s >= 200 && s < 300) || s === 401,
                suppress401Log: true,
            }
        );
        return res.status === 401 ? null : res.data.dealer ?? null;
    } catch {
        return null;
    }
}


export async function debugDealerCookies(): Promise<unknown> {
    try {
        const res = await http.get("/dealers-auth/auth/debug-cookies");
        return res.data;
    } catch (e) {
        console.error("[DEBUG] Dealer cookies → ERROR", e);
        throw e;
    }
}

export async function logoutDealer(): Promise<void> {
    try {
        await http.post<{ ok: true }>("/dealers-auth/logout");
    } catch (e) {
        throw new Error(pickErrorMessage(e));
    }
}